import React, {useCallback, useContext, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';

import {Button, ButtonGroup, InlineNotification, Modal, TextBox, TextBoxProps} from '@veneer/core';

import {FetchError, License, MinimalTenant, ResponseError} from '@br/idm-api';
import {useCustomerApi} from '@br/idm-components';
import {useWolfTheme} from '@br/mg-components';

import {GlobalTranslationsContext} from '../../../i18n';
import {assertIsNotNullish, flattenErrorResponse, useFormatDetails} from '../../../util';

const TransferLicenseModalStep = {
    form: 0,
    confirm: 1,
    complete: 2,
} as const;
type TransferLicenseModalStep = (typeof TransferLicenseModalStep)[keyof typeof TransferLicenseModalStep];

export const TransferLicenseModal = ({
    onClose,
    targetLicense,
}: {
    onClose: () => void;
    targetLicense: License | undefined;
}): JSX.Element => {
    const theme = useWolfTheme();

    const {t} = useTranslation(['license']);
    const {veneer: veneerTranslations} = useContext(GlobalTranslationsContext);
    const {tenantApi} = useCustomerApi();
    const formatDetails = useFormatDetails();

    const [step, setStep] = useState<TransferLicenseModalStep>(TransferLicenseModalStep.form);
    const [targetTenant, setTargetTenant] = useState<MinimalTenant>();
    const [targetTenantId, setTargetTenantId] = useState('');
    const [tenantError, setTenantError] = useState('');
    const [responseError, setResponseError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleValidate = useCallback(() => {
        setLoading(true);
        setResponseError('');

        Promise.all([
            tenantApi.tenantLookupRetrieve({
                lookup: targetTenantId,
            }),
            // Make the submission last at least 1.5 seconds to improve visuals
            new Promise(res => setTimeout(res, 1500)),
        ])
            .then(results => {
                const tenant: MinimalTenant = results[0];
                setTargetTenant(tenant);
                setStep(TransferLicenseModalStep.confirm);
            })
            .catch(async (error: ResponseError) => {
                if (error.response.status === 404) {
                    setResponseError(
                        t(
                            'license:notFound',
                            'A tenant with that ID was not found. Please check the value and try again.'
                        )
                    );
                } else {
                    await flattenErrorResponse(error).then(errors => {
                        setResponseError(errors.join('\n'));
                    });
                }
            })
            .finally(() => {
                setLoading(false);
            });
    }, [t, targetTenantId, tenantApi]);

    const handleConfirm = useCallback(() => {
        setLoading(true);
        setResponseError('');

        assertIsNotNullish(targetLicense);
        assertIsNotNullish(targetLicense.tenant);
        assertIsNotNullish(targetTenant);

        Promise.all([
            tenantApi.tenantLicenseUpdate({
                id: targetLicense.id,
                tenantId: targetLicense.tenant.id,
                licenseUpdateRequest: {
                    tenantId: targetTenant.id,
                },
            }),
            // Make the submission last at least 1.5 seconds to improve visuals
            new Promise(res => setTimeout(res, 1500)),
        ])
            .then(() => {
                setResponseError('');
                setStep(TransferLicenseModalStep.complete);
            })
            .catch(async (error: FetchError | ResponseError) => {
                // Validation error: License is not valid for target tenant (WPS vs WPSS)
                await flattenErrorResponse(error).then(errors => {
                    setResponseError(errors.join('\n'));
                });
            })
            .finally(() => {
                setLoading(false);
            });
    }, [targetLicense, targetTenant, tenantApi]);

    const handleTargetTenantIdChange = useCallback<Required<TextBoxProps>['onChange']>(
        value => {
            // Ensure the tenant ID isn't the same as the current one
            if (value.toLocaleLowerCase() === targetLicense?.tenant?.shortId.toLocaleLowerCase()) {
                setTenantError(
                    t(
                        'license:cannotTransferToSelf',
                        'You cannot transfer a license to its own tenant, please enter a different ID.'
                    )
                );
            } else if (value.length === 8 && /^[0-9a-fA-F]{8}$/.exec(value) === null) {
                setTenantError(
                    t(
                        'license:invalidIDFormat',
                        'Invalid tenant ID. It should be eight characters long and comprised of the characters ABCDEF1234567890.'
                    )
                );
            } else {
                setTenantError('');
            }

            setTargetTenantId(value);
        },
        [t, targetLicense]
    );

    const handleOnClose = useCallback(() => {
        setStep(TransferLicenseModalStep.form);
        setTargetTenant(undefined);
        setLoading(false);
        setTargetTenantId('');
        setTenantError('');
        setResponseError('');
        onClose();
    }, [onClose]);

    const confirmDetails = [
        {label: t('license:licenseNumber', 'License Number'), value: targetLicense?.licenseNumber},
        {label: t('license:licenseType', 'Product'), value: targetLicense?.product.name},
        {label: t('license:purchased', 'Purchased'), value: targetLicense?.deviceCount?.toString()},
        {label: t('license:used', 'Used'), value: targetLicense?.seatsInUse.toString()},
        {label: t('license:tenantName', 'Tenant Name'), value: targetTenant?.name},
        {label: t('license:tenantID', 'Tenant ID'), value: targetTenant?.shortId},
    ].filter(d => d.value);

    return (
        <Modal
            id="license-transfer-modal"
            onClose={handleOnClose}
            show={targetLicense !== undefined}
            title={t('license:transferLicense', 'Transfer license')}
            footer={
                <ButtonGroup>
                    {step === TransferLicenseModalStep.form ? (
                        <Button
                            appearance="primary"
                            onClick={handleValidate}
                            disabled={targetTenantId.length !== 8 || tenantError.length > 0}
                            loading={loading}
                        >
                            {t('license:validate', 'Validate')}
                        </Button>
                    ) : null}
                    {step === TransferLicenseModalStep.confirm ? (
                        <Button
                            appearance="primary"
                            onClick={handleConfirm}
                            loading={loading}
                        >
                            {t('license:confirm', 'Confirm')}
                        </Button>
                    ) : null}
                    {step === TransferLicenseModalStep.complete ? (
                        <Button
                            appearance="primary"
                            onClick={handleOnClose}
                        >
                            {t('license:finish', 'Finish')}
                        </Button>
                    ) : null}
                    {step === TransferLicenseModalStep.form || step === TransferLicenseModalStep.confirm ? (
                        <Button
                            appearance="secondary"
                            disabled={loading}
                            onClick={handleOnClose}
                        >
                            {t('license:cancel', 'Cancel')}
                        </Button>
                    ) : null}
                </ButtonGroup>
            }
        >
            {step === TransferLicenseModalStep.form ? (
                <>
                    <Trans i18nKey="license:transferFormDescription">
                        <p>This process allows you to transfer this license from your account to another.</p>
                        <p>
                            To begin, you will need to know the eight character Tenant ID of the organization to which
                            you would like to transfer the license. They can find this on the Licenses page of their
                            account.
                        </p>
                        <p>
                            Any devices associated with this license will have their license removed and will request a
                            new one. If you do not have enough seats free in your account, those devices will become
                            unlicensed.
                        </p>
                        <p>
                            <strong>Note:</strong> After the completion of this process, the license ownership will
                            change, and only the new owner can reverse the process.
                        </p>
                    </Trans>

                    <hr className="mt-0 mb-7" />

                    <p
                        style={{color: theme.color.gray6}}
                        className="mb-2 pb-0 d-block"
                    >
                        {t('license:licenseNumber', 'License Number')}
                    </p>
                    <p className="mb-6">{targetLicense?.licenseNumber}</p>

                    {responseError.length > 0 ? (
                        <InlineNotification
                            hideIcon={false}
                            closeButton={false}
                            title={responseError}
                            type="negative"
                            i18n={veneerTranslations.inlineNotification}
                        />
                    ) : null}

                    <TextBox
                        className="mt-4"
                        label={t('license:targetTenantId', 'Destination Tenant ID')}
                        data-testid="target-tenant-id"
                        id="target-tenant-id"
                        maxLength={8}
                        onChange={handleTargetTenantIdChange}
                        value={targetTenantId}
                        error={tenantError.length > 0}
                        helperText={
                            tenantError.length > 0
                                ? tenantError
                                : t(
                                      'license:targetTenantIdHelp',
                                      'The eight-character ID of the organization you would like to transfer this license to. e.g. ABCD1234'
                                  )
                        }
                        helperTextVisibility="visible"
                        separateLabel
                    />
                </>
            ) : null}

            {step === TransferLicenseModalStep.confirm ? (
                <>
                    {responseError.length > 0 ? (
                        <InlineNotification
                            hideIcon={false}
                            closeButton={false}
                            title={responseError}
                            type="negative"
                            i18n={veneerTranslations.inlineNotification}
                        />
                    ) : null}

                    <Trans i18nKey="license:confirmationText">
                        <p>Before submitting this form, please check the details below carefully.</p>
                        <p>
                            Once you submit this form, only the new owner of the license will be able to reverse the
                            process.
                        </p>
                    </Trans>
                    <div className="mx-n2 mt-2 d-flex flex-wrap">{confirmDetails.map(formatDetails)}</div>
                </>
            ) : null}

            {step === TransferLicenseModalStep.complete ? (
                <>
                    <Trans
                        i18nKey="license:completionText"
                        shouldUnescape
                    >
                        <p>
                            Your license has been successfully transferred to &quot;{{name: targetTenant?.name}}&quot;
                        </p>
                        <p>You may now close this window.</p>
                    </Trans>
                </>
            ) : null}
        </Modal>
    );
};

export default TransferLicenseModal;
